import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  browserNotificationRuntime,
  type BrowserInstallPromptChoice,
  type BrowserNotificationRuntime,
} from '@/lib/browser-notification-runtime'
import {
  installationInstructionContent,
  MANUAL_INSTALLATION_INSTRUCTION_FAMILIES,
  type ManualInstallationInstructionFamily,
  resolveInstallationGuidance,
} from '@/lib/installation-guidance'
import { useI18n } from '@/lib/i18n'
import { useAuth } from '@/lib/auth'
import {
  type WebPushPreferenceCategory,
  type WebPushViewModel,
  useWebPush,
} from '@/hooks/use-web-push'
import type { WebPushUiState } from '@/lib/web-push-state'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

type InstallResult = BrowserInstallPromptChoice['outcome'] | 'error' | null

export interface InstallationGuidanceCardProps {
  runtime?: BrowserNotificationRuntime
}

function resultKey(result: InstallResult): string | null {
  if (result === 'accepted')
    return 'installationGuidance.installAccepted'
  if (result === 'dismissed')
    return 'installationGuidance.installDismissed'
  if (result === 'error')
    return 'installationGuidance.installError'
  return null
}

export function InstallationGuidanceCard({
  runtime = browserNotificationRuntime,
}: InstallationGuidanceCardProps) {
  const { t } = useI18n()
  const [installPrompt, setInstallPrompt] = useState(() =>
    runtime.currentInstallPrompt(),
  )
  const [manualOverride, setManualOverride]
    = useState<ManualInstallationInstructionFamily | null>(null)
  const [showBrowserChoice, setShowBrowserChoice] = useState(false)
  const [installResult, setInstallResult] = useState<InstallResult>(null)
  const installPromptInFlight = useRef(false)

  useEffect(
    () =>
      runtime.listenForInstallPrompt((prompt) => {
        if (prompt)
          installPromptInFlight.current = false
        setInstallPrompt(prompt)
      }),
    [runtime],
  )

  const capabilities = runtime.readCapabilities()
  const resolution = resolveInstallationGuidance({
    capabilities,
    permission: runtime.notificationPermission(),
    installPromptAvailable: !!installPrompt,
    hints: runtime.readInstallationHints(),
    manualOverride,
  })

  const invokeInstallPrompt = () => {
    if (!installPrompt || installPromptInFlight.current)
      return

    const prompt = installPrompt
    installPromptInFlight.current = true
    setInstallPrompt(null)
    setInstallResult(null)
    prompt
      .prompt()
      .then(() => prompt.userChoice)
      .then(choice => setInstallResult(choice.outcome))
      .catch(() => setInstallResult('error'))
  }

  if (resolution.phase === 'standalone') {
    return (
      <Card data-testid="installation-guidance">
        <CardHeader>
          <CardTitle>
            <h2>{t('installationGuidance.installedTitle')}</h2>
          </CardTitle>
          <CardDescription>
            {t(
              resolution.notificationApiSurfaceExposed
                ? 'installationGuidance.installedDescription'
                : 'installationGuidance.installedApiSurfaceIncompleteDescription',
            )}
          </CardDescription>
        </CardHeader>
        {resolution.notificationApiSurfaceExposed && (
          <CardContent>
            <section
              className="rounded-lg border bg-muted/30 p-4"
              aria-labelledby="notification-api-surface-title"
            >
              <h3 id="notification-api-surface-title" className="font-medium">
                {t('installationGuidance.apiSurfaceTitle')}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('installationGuidance.apiSurfaceDescription')}
              </p>
            </section>
          </CardContent>
        )}
      </Card>
    )
  }

  const instructionFamily = resolution.instructionFamily ?? 'generic'
  const content = installationInstructionContent(instructionFamily)
  const currentResultKey = resultKey(installResult)

  return (
    <Card data-testid="installation-guidance">
      <CardHeader>
        <CardTitle>
          <h2>{t('installationGuidance.title')}</h2>
        </CardTitle>
        <CardDescription>
          {t('installationGuidance.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {resolution.notificationApiSurfaceExposed && (
          <section
            className="rounded-lg border bg-muted/30 p-4"
            aria-labelledby="notification-api-surface-title"
          >
            <h3 id="notification-api-surface-title" className="font-medium">
              {t('installationGuidance.apiSurfaceTitle')}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('installationGuidance.apiSurfaceDescription')}
            </p>
          </section>
        )}

        <section aria-labelledby="installation-steps-title">
          <h3 id="installation-steps-title" className="font-medium">
            {t('installationGuidance.stepsTitle')}
          </h3>
          <p className="mt-1 text-sm font-medium text-primary">
            {t(content.familyLabelKey)}
          </p>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm">
            {content.stepKeys.map(key => (
              <li key={key}>{t(key)}</li>
            ))}
          </ol>
          {content.fallbackKey && (
            <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100">
              {t(content.fallbackKey)}
            </p>
          )}
        </section>

        {resolution.installAction === 'browser-prompt' && installPrompt && (
          <Button
            type="button"
            className="min-h-11"
            onClick={invokeInstallPrompt}
          >
            {t('installationGuidance.installButton')}
          </Button>
        )}

        <div aria-live="polite">
          {currentResultKey && (
            <p className="text-sm text-muted-foreground">
              {t(currentResultKey)}
            </p>
          )}
        </div>

        <div className="border-t pt-4">
          <Button
            type="button"
            variant="link"
            className="h-auto min-h-11 whitespace-normal px-0 py-2 text-left"
            aria-expanded={showBrowserChoice}
            aria-controls="installation-browser-choice"
            onClick={() => setShowBrowserChoice(value => !value)}
          >
            {t('installationGuidance.wrongBrowser')}
          </Button>

          {showBrowserChoice && (
            <div
              id="installation-browser-choice"
              className="mt-3 space-y-3 rounded-lg border p-4"
            >
              <label
                htmlFor="installation-browser-family"
                className="block text-sm font-medium"
              >
                {t('installationGuidance.browserChoiceLabel')}
              </label>
              <select
                id="installation-browser-family"
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={manualOverride ?? ''}
                onChange={event =>
                  setManualOverride(
                    event.target.value as ManualInstallationInstructionFamily,
                  )}
              >
                <option value="" disabled>
                  {t(content.familyLabelKey)}
                </option>
                {MANUAL_INSTALLATION_INSTRUCTION_FAMILIES.map(family => (
                  <option key={family} value={family}>
                    {t(installationInstructionContent(family).familyLabelKey)}
                  </option>
                ))}
              </select>
              {manualOverride && (
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11"
                  onClick={() => setManualOverride(null)}
                >
                  {t('installationGuidance.useDetectedBrowser')}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function NotificationTab() {
  const { user, browserSessionId } = useAuth()

  return (
    <div className="space-y-6">
      {user && browserSessionId && (
        <WebPushSettings
          expectedUserId={user.id}
          expectedSessionId={browserSessionId}
        />
      )}
      <InstallationGuidanceCard />
    </div>
  )
}

const STATE_KEY_SUFFIX: Record<WebPushUiState, string> = {
  'unsupported': 'unsupported',
  'ios-not-installed': 'iosNotInstalled',
  'permission-default': 'permissionDefault',
  'permission-denied': 'permissionDenied',
  'permission-granted-unregistered': 'permissionGrantedUnregistered',
  'active': 'active',
  'locally-disabled': 'locallyDisabled',
  'temporarily-unhealthy': 'temporarilyUnhealthy',
  'server-invalidated': 'serverInvalidated',
}

function WebPushSettings({
  expectedUserId,
  expectedSessionId,
}: {
  expectedUserId: string
  expectedSessionId: string
}) {
  const viewModel = useWebPush(expectedUserId, expectedSessionId)
  return <WebPushSettingsCard viewModel={viewModel} />
}

export function WebPushSettingsCard({
  viewModel,
}: {
  viewModel: WebPushViewModel
}) {
  const { t } = useI18n()
  const stateSuffix = STATE_KEY_SUFFIX[viewModel.state]
  const events = [
    'gameStarted',
    'turnReady',
    'gameFinished',
    'friendsMessage',
    'systemMessage',
  ] as const
  const preferences: Array<{
    category: WebPushPreferenceCategory
    labelKey: string
    hintKey: string
  }> = [
    {
      category: 'global',
      labelKey: 'pushNotifications.preferences.globalLabel',
      hintKey: 'pushNotifications.preferences.globalHint',
    },
    {
      category: 'game',
      labelKey: 'pushNotifications.preferences.gameLabel',
      hintKey: 'pushNotifications.preferences.gameHint',
    },
    {
      category: 'friends',
      labelKey: 'pushNotifications.preferences.friendsLabel',
      hintKey: 'pushNotifications.preferences.friendsHint',
    },
    {
      category: 'system',
      labelKey: 'pushNotifications.preferences.systemLabel',
      hintKey: 'pushNotifications.preferences.systemHint',
    },
  ]

  return (
    <Card data-testid="web-push-settings">
      <CardHeader>
        <CardTitle>
          <h2>{t('pushNotifications.title')}</h2>
        </CardTitle>
        <CardDescription>{t('pushNotifications.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <section aria-labelledby="web-push-events-title">
          <h3 id="web-push-events-title" className="font-medium">
            {t('pushNotifications.eventsTitle')}
          </h3>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm">
            {events.map(event => (
              <li key={event}>{t(`pushNotifications.events.${event}`)}</li>
            ))}
          </ul>
          <p className="mt-4 rounded-lg border border-amber-500/40 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100">
            {t('pushNotifications.emailDisclosure')}
          </p>
        </section>

        <section
          className="rounded-lg border bg-muted/30 p-4"
          aria-labelledby="web-push-status-title"
          role="status"
          aria-live="polite"
        >
          <h3 id="web-push-status-title" className="font-medium">
            {t('pushNotifications.statusLabel')}
          </h3>
          {viewModel.statusPending ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              {t('pushNotifications.checking')}
            </p>
          ) : (
            <div className="mt-2">
              <p className="text-sm font-medium">
                {t(`pushNotifications.states.${stateSuffix}.title`)}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t(`pushNotifications.states.${stateSuffix}.description`)}
              </p>
            </div>
          )}
        </section>

        {viewModel.state === 'permission-denied' && (
          <p className="rounded-lg border px-3 py-2 text-sm">
            {t('pushNotifications.permissionRecovery')}
          </p>
        )}

        {!viewModel.serviceAvailable && !viewModel.statusPending && (
          <p className="text-sm text-muted-foreground">
            {t('pushNotifications.serviceUnavailable')}
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          {viewModel.canEnable && (
            <Button
              type="button"
              className="min-h-11"
              disabled={viewModel.actionPending}
              onClick={() => {
                viewModel.enable().catch(() => {})
              }}
            >
              {viewModel.actionPending && (
                <Loader2
                  className="mr-2 size-4 animate-spin"
                  aria-hidden="true"
                />
              )}
              {t('pushNotifications.enable')}
            </Button>
          )}
          {viewModel.canDisableCurrentDevice && (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={viewModel.actionPending}
              onClick={() => {
                viewModel.disableCurrentDevice().catch(() => {})
              }}
            >
              {t('pushNotifications.disableDevice')}
            </Button>
          )}
        </div>

        {viewModel.actionError && (
          <p className="text-sm text-destructive" role="alert">
            {t('pushNotifications.actionError')}
          </p>
        )}

        <fieldset className="space-y-4 border-t pt-5">
          <legend className="font-medium">
            {t('pushNotifications.preferences.title')}
          </legend>
          <p className="text-sm text-muted-foreground">
            {t('pushNotifications.preferences.description')}
          </p>
          {viewModel.preferencesPending ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              {t('pushNotifications.checking')}
            </p>
          ) : viewModel.preferencesLoadError ? (
            <p className="text-sm text-destructive" role="alert">
              {t('pushNotifications.preferences.loadError')}
            </p>
          ) : (
            preferences.map((preference) => {
              const pending = viewModel.preferencePending !== null
              return (
                <div
                  key={preference.category}
                  className="flex items-start gap-3"
                >
                  <Checkbox
                    id={`push-preference-${preference.category}`}
                    data-testid={`push-preference-${preference.category}`}
                    className="mt-1"
                    checked={viewModel.preferences[preference.category]}
                    disabled={pending}
                    onCheckedChange={(checked) => {
                      viewModel.setPreference(
                        preference.category,
                        checked === true,
                      )
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <label
                      htmlFor={`push-preference-${preference.category}`}
                      className="cursor-pointer text-sm font-medium"
                    >
                      {t(preference.labelKey)}
                    </label>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t(preference.hintKey)}
                    </p>
                  </div>
                </div>
              )
            })
          )}
          {viewModel.preferenceError && (
            <p className="text-sm text-destructive" role="alert">
              {t('pushNotifications.preferences.saveError')}
            </p>
          )}
        </fieldset>
      </CardContent>
    </Card>
  )
}
